// campaign-time-sync.ts — my_campaign GM time-control UI sync + cache-first campaign fetch, carved out of
// template.js (roadmap T4.5, 2026-06-16).
// Mostly pure DOM display/classList sync against window.FateTide.* (no queryData); getCampaignArr +
// syncTimeControlsForEncounterId are async fetch/orchestrate. All six are window-attached at the bottom.

/**
 * my_campaign: fast-forward is for long narrative time steps; hide it during tactical combat.
 */
function setMyCampaignFastForwardWrapHidden(hidden: unknown): void {
	const wrap = document.getElementById('wrap_campaign_fast_forward');
	if (!wrap) return;
	wrap.style.display = hidden ? 'none' : '';
}

/**
 * my_campaign — collapse #ft_campaign_time_spinner_slot when the clock GIF is hidden (idle),
 * freeing horizontal gutter for toolbar buttons.
 */
function syncMyCampaignTimeSpinnerSlot(): void {
	const gif = document.getElementById('div_time_moving');
	const slot = document.getElementById('ft_campaign_time_spinner_slot');
	if (!gif || !slot) return;
	slot.classList.toggle(
		'ft-campaign-time-spinner-slot--active',
		gif.style.display === 'block',
	);
}

/**
 * my_campaign — pause / play / stop cluster. Stop is shown ONLY during an active multi-chunk GM time push
 * (heartbeat AJAX steps); idle fast-forward mode never shows Stop — use Play when time is not advancing.
 */
function syncMyCampaignPlayPauseStopCluster(): void {
	const play = document.getElementById('play_campaign_time');
	const pause = document.getElementById('pause_campaign_time');
	const stop = document.getElementById('stop_campaign_time_skip');
	const inCombatBtn = document.getElementById('btn_in_combat_time_control');
	if (!play || !pause || !stop || !inCombatBtn) return;

	initializeFateTideCaches();

	const inCombat =
		window.FateTide._myCampaignTimeControlsInCombat === true;

	if (inCombat) {
		stop.style.display = 'none';
		window.FateTide.cancelCampaignTimeSkipRequested = false;
		return;
	}

	const chunkStop = !!window.FateTide.campaignTimeChunkStopUiActive;
	const pauseShowing = pause.style.display === 'block';

	if (!chunkStop) {
		stop.style.display = 'none';
		if (pauseShowing) {
			play.style.display = 'none';
		} else {
			pause.style.display = 'none';
			play.style.display = '';
		}
		return;
	}

	stop.style.display = '';
	if (pauseShowing) {
		play.style.display = 'none';
	} else {
		pause.style.display = 'none';
		play.style.display = 'none';
	}
}


/**
 * my_campaign: align pause/play, in-combat time affordance, and push/pull rows with
 * the active encounter’s in_combat (e.g. after loading another encounter while combat UI was left up).
 * No-op on pages that do not have the GM time control elements.
 */
function syncMyCampaignTimeControlsToEncounterInCombat(encounterArr: unknown): void {
	const play = document.getElementById('play_campaign_time');
	if (!play) return;

	initializeFateTideCaches();

	const pause = document.getElementById('pause_campaign_time');
	const inCombatBtn = document.getElementById('btn_in_combat_time_control');
	const divMoving = document.getElementById('div_time_moving');
	if (!pause || !inCombatBtn || !divMoving) return;

	const inCombat = parseInt(String(ftEncounterField(encounterArr, 'in_combat') ?? '0'), 10) === 1;

	const prev = window.FateTide._myCampaignTimeControlsInCombat;
	const inCombatChanged = prev !== undefined && prev !== inCombat;
	window.FateTide._myCampaignTimeControlsInCombat = inCombat;

	if (inCombat) {
		play.style.display = 'none';
		pause.style.display = 'none';
		inCombatBtn.style.display = 'block';
		divMoving.style.display = 'none';
		window.FateTide.cancelCampaignTimeSkipRequested = false;
		window.FateTide.campaignTimeChunkStopUiActive = false;
		document.querySelectorAll<HTMLElement>('.tr_campaign_time_control').forEach((el) => {
			el.style.display = 'none';
		});
		setMyCampaignFastForwardWrapHidden(true);
	} else {
		// OOC: only react when in_combat actually flips, so encounter_update heartbeats (time, light, status
		// from push_time) do not keep resetting play / pause and the "running" clock affordance.
		if (!inCombatChanged) {
			syncMyCampaignPlayPauseStopCluster();
			syncMyCampaignTimeSpinnerSlot();
			return;
		}
		// Left combat: exitCombat() already set pause + moving + pull/push rows. Do not clobber.
		if (prev && pause.style.display === 'block') {
			syncMyCampaignPlayPauseStopCluster();
			syncMyCampaignTimeSpinnerSlot();
			return;
		}
		// e.g. loaded / switched to an OOC encounter while UI still showed in-combat chrome
		play.style.display = '';
		pause.style.display = 'none';
		inCombatBtn.style.display = 'none';
		divMoving.style.display = 'none';
		document.querySelectorAll<HTMLElement>('.tr_campaign_time_control').forEach((el) => {
			el.style.display = '';
		});
		setMyCampaignFastForwardWrapHidden(false);
	}
	syncMyCampaignPlayPauseStopCluster();
	syncMyCampaignTimeSpinnerSlot();
}


/** Fetch encounter_arr for encId and sync GM time control visibility; updates encounterCache. */
async function syncTimeControlsForEncounterId(encId: unknown): Promise<void> {
	if (!encId) return;
	const encounterId = String(encId);
	const enc = await getEncounterArr(encounterId);
	if (!enc) return;
	if (window.FateTide?.encounterCache instanceof Map) {
		window.FateTide.encounterCache.set(encounterId, enc);
		// Whole-blob replacement → re-point casterCache aliases at the fresh creature rows
		// (websocket bundle helper; typeof-guarded for load order).
		if (typeof window.ftRealiasCastersFromEncounterBlob === 'function') {
			window.ftRealiasCastersFromEncounterBlob(enc);
		}
	}
	syncMyCampaignTimeControlsToEncounterInCombat(enc);
}


const campaignArrReads = new Map<string, Promise<unknown | null>>();
const campaignArrWrites = new Map<string, { sequence: number; result: unknown }>();
let campaignArrReadSequence = 0;
let campaignArrWriteSequence = 0;
async function getCampaignArr(campaign_id: unknown, bustCache: boolean = false): Promise<unknown | null> {
    if (!campaign_id) {
        console.error('No campaign_id available for getCampaignArr');
        return null;
    }

    // Cache-first (2026-06-13): campaignCache is kept current by the WS deltas and campaign rarely
    // changes mid-session, so a HIT is server-truth without a round-trip. Only a miss or an explicit
    // bustCache hits the wire. (Retires the old-refresh-era unconditional get_campaign_arr POST.)
    initializeFateTideCaches();
    if (!bustCache) {
        const campaignId = String(campaign_id);
        const cached = window.FateTide.campaignCache.get(campaignId);
        if (cached) {
            window.FateTide.campaignArr = cached;
            campaignArrWriteSequence = ++campaignArrReadSequence;
            return cached;
        }
        // A miss joins a read already on the wire: page boot, the clock boot and the scene's setting summary all asked at load,
        // each before the first answer was cached, and each read the same campaign (2026-10-26 audit). A bust always reads anew.
        const running = campaignArrReads.get(String(campaign_id));
        if (running) return running;
    }
    const key = String(campaign_id), read = fetchCampaignArr(campaign_id);
    campaignArrReads.set(key, read);
    const release = () => { if (campaignArrReads.get(key) === read) campaignArrReads.delete(key); };
    // Like getEncounterArr, stop sharing a stalled read after 30 s without cancelling its existing callers.
    const timer = setTimeout(release, 30_000);
    void read.finally(() => { clearTimeout(timer); release(); });
    return read;
}

async function fetchCampaignArr(campaign_id: unknown): Promise<unknown | null> {
    try {
        const key = String(campaign_id), sequence = ++campaignArrReadSequence;
        const cachedBefore = window.FateTide.campaignCache.get(key);
        const response = await fetch(`${BASE_URL}options/tools/campaigns/select/get_campaign_arr.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                campaign_id: key
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const raw: unknown = await response.json(); // Expecting pure JSON campaign_arr
        const result = raw !== null && typeof raw === 'object' && !Array.isArray(raw)
            ? raw as Record<string, unknown>
            : {};

        ftApplyCampaignPermissionForViewer(result);

        const cachedNow = window.FateTide.campaignCache.get(key), lastWrite = campaignArrWrites.get(key);
        // Newer reads win once installed. campaign_update writes a new object directly into the cache:
        // a changed reference that isn't our last read's result supersedes every read already in flight.
        if ((lastWrite?.sequence ?? 0) > sequence
            || (cachedNow !== cachedBefore && cachedNow !== lastWrite?.result)) return result;

        // The singleton alias spans campaigns. Preserve later selections, but let an in-flight refresh
        // replace its own cached snapshot even when a cache hit selected that snapshot in the meantime.
        if (sequence > campaignArrWriteSequence || window.FateTide.campaignArr === cachedBefore) {
            window.FateTide.campaignArr = result;
            campaignArrWriteSequence = Math.max(campaignArrWriteSequence, sequence);
        }
        // Campaign reshape (2026-06-04): campaign_id lives in result.campaigns (bare scalar);
        // bucket-first with a legacy [0] fallback for un-rolled blobs.
        const campaigns = result.campaigns !== null && typeof result.campaigns === 'object' && !Array.isArray(result.campaigns)
            ? result.campaigns as Record<string, unknown>
            : null;
        const legacyId = Array.isArray(result.campaign_id) ? result.campaign_id[0] : result.campaign_id;
        const _resCampaignId = campaigns?.campaign_id ?? legacyId;
        if (_resCampaignId) {
            initializeFateTideCaches();
            window.FateTide.campaignCache.set(String(_resCampaignId), result);
        }
        campaignArrWrites.set(key, { sequence, result });

        return result;

    } catch (error) {
        console.error("Error fetching campaign_arr:", error);
        return null;
    }
}

// ===========================================================================================
// Window attachment — the public surface the original top-level functions exposed. All six were globals and
// every one has out-of-bundle consumers (my_campaign.php inline / campaign-time-init.js / websocket bundle /
// side-panels-init.js / roll-init.js + staying template.js callers), reached at runtime.
// ===========================================================================================
declare global {
    interface Window {
        setMyCampaignFastForwardWrapHidden: typeof setMyCampaignFastForwardWrapHidden;
        syncMyCampaignTimeSpinnerSlot: typeof syncMyCampaignTimeSpinnerSlot;
        syncMyCampaignPlayPauseStopCluster: typeof syncMyCampaignPlayPauseStopCluster;
        syncMyCampaignTimeControlsToEncounterInCombat: typeof syncMyCampaignTimeControlsToEncounterInCombat;
        syncTimeControlsForEncounterId: typeof syncTimeControlsForEncounterId;
        getCampaignArr: typeof getCampaignArr;
    }
}

Object.assign(window, {
    setMyCampaignFastForwardWrapHidden,
    syncMyCampaignTimeSpinnerSlot,
    syncMyCampaignPlayPauseStopCluster,
    syncMyCampaignTimeControlsToEncounterInCombat,
    syncTimeControlsForEncounterId,
    getCampaignArr,
});

export {};
